import { keyframes } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Footer, Header, Loading } from '@/core/components';
import { useRace } from '@/core/contexts/RaceContext';
import { useAuth, useDocumentTitle, useFavicon } from '@/core/hooks';
import type { Claim } from '@/core/types';
import { BibSearchBar, CertCard, CertDetailDialog, CertGrid } from './components';
import { useCerts } from './hooks';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export function CertsPage() {
  const { race } = useRace();
  const { user } = useAuth();
  const theme = useTheme();
  useDocumentTitle(`${race.name} みんなの完走証 | RunPort NFT`);
  useFavicon(`/races/${race.alias}/favicon.ico`);
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const { certs, loading, error, myCert, shuffle, searchBib, clearHighlight, searchedCert, clearSearchedCert } =
    useCerts({
      raceAlias: race.alias,
      uid: user?.uid,
    });

  // 検索結果が一覧に含まれているかチェック
  const isSearchedCertInCerts = searchedCert && certs.some((c) => c.id === searchedCert.id);

  // 表示する完走証リストとハイライトIDを決定
  // - 検索時: 検索結果を先頭にハイライト、自分の完走証は含めない
  // - 通常時: 自分の完走証を先頭にハイライト
  const displayCerts = searchedCert
    ? isSearchedCertInCerts
      ? [searchedCert, ...certs.filter((c) => c.id !== searchedCert.id)]
      : certs
    : myCert
      ? [myCert, ...certs]
      : certs;

  const effectiveHighlightedId = searchedCert ? searchedCert.id : myCert?.id || null;

  // 検索結果が表示一覧に含まれているかチェック（別枠表示の判定用）
  const isSearchedCertInCurrentPage = searchedCert && displayCerts.some((c) => c.id === searchedCert.id);

  const [searchLoading, setSearchLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCardClick = (claim: Claim) => {
    setSelectedClaim(claim);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedClaim(null);
  };

  const handleSearch = async (bib: string) => {
    setSearchLoading(true);
    clearHighlight();
    clearSearchedCert();

    try {
      const result = await searchBib(bib);
      if (result.error) {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error',
        });
      } else if (result.found) {
        setSnackbar({
          open: true,
          message: `ゼッケン ${bib} の完走証が見つかりました`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `ゼッケン ${bib} の完走証は見つかりませんでした`,
          severity: 'warning',
        });
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header titleImage={`/races/${race.alias}/title.png`} titleImageSm={`/races/${race.alias}/title_sm.png`} />

      <Container component="main" maxWidth="lg" sx={{ flex: 1, py: 3 }}>
        {/* タイトルエリア */}
        <Box
          sx={{
            textAlign: 'center',
            mt: { xs: 2, sm: 4 },
            mb: { xs: 3, sm: 4 },
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: '0.2em',
              color: 'text.secondary',
              mb: 1,
              fontSize: '0.7rem',
            }}
          >
            EVERYONE'S CERTIFICATES
          </Typography>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              background: gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            みんなの完走証
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {race.name}
          </Typography>
        </Box>

        {/* 検索バー */}
        <Box sx={{ mb: 2, animation: `${fadeInUp} 0.6s ease-out 0.1s both` }}>
          <BibSearchBar onSearch={handleSearch} loading={searchLoading} />
        </Box>

        {/* シャッフルボタン */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
            animation: `${fadeInUp} 0.6s ease-out 0.15s both`,
          }}
        >
          <Button variant="outlined" startIcon={<ShuffleIcon />} onClick={shuffle} disabled={loading} sx={{ mb: 0.5 }}>
            シャッフル
          </Button>
          <Typography variant="caption" color="text.secondary">
            他の完走証も見てみよう
          </Typography>
        </Box>

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
            {error}
          </Alert>
        )}

        {/* 検索結果（表示一覧にない場合のみ表示） */}
        {searchedCert && !isSearchedCertInCurrentPage && (
          <Box
            sx={{
              mb: 4,
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              animation: `${fadeInUp} 0.4s ease-out`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                検索結果
              </Typography>
              <IconButton size="small" onClick={clearSearchedCert} sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ maxWidth: 160 }}>
              <CertCard claim={searchedCert} highlighted onClick={() => handleCardClick(searchedCert)} />
            </Box>
          </Box>
        )}

        {/* コンテンツ */}
        {loading ? (
          <Loading />
        ) : (
          <CertGrid certs={displayCerts} highlightedId={effectiveHighlightedId} onCardClick={handleCardClick} />
        )}
      </Container>

      <Footer />

      {/* 検索結果スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 詳細ダイアログ */}
      <CertDetailDialog claim={selectedClaim} open={dialogOpen} onClose={handleDialogClose} />
    </Box>
  );
}
